import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/* ─── Costanti piattaforma ─────────────────────────────────────────────── */
const COLORS = {
  instagram:      [221, 42, 123],
  facebook:       [24, 119, 242],
  tiktok:         [254, 44, 85],
  youtube:        [255, 0, 0],
  twitter:        [29, 155, 240],
  linkedin:       [10, 102, 194],
  google_business:[66, 133, 244],
  ads:            [0, 180, 220],
};
const LABELS = {
  instagram:'Instagram', facebook:'Facebook', tiktok:'TikTok',
  youtube:'YouTube', twitter:'X (Twitter)', linkedin:'LinkedIn',
  google_business:'Google Business', ads:'Meta Ads',
};
const KPIS = {
  instagram: [
    ['followers_count','Follower','delta'],
    ['reach_1d','Reach giornaliera','sum'],
    ['profile_views_1d','Visite profilo','sum'],
    ['total_interactions_1d','Interazioni','sum'],
  ],
  facebook: [
    ['followers_count','Follower','delta'],
    ['reach_1d','Reach giornaliera','sum'],
    ['total_interactions_1d','Interazioni','sum'],
  ],
  tiktok: [
    ['followers_count','Follower','delta'],
    ['video_views_1d','Video Views','sum'],
    ['likes_1d','Like','sum'],
    ['shares_1d','Condivisioni','sum'],
  ],
  youtube: [
    ['subscribers','Iscritti','delta'],
    ['views_1d','Views','sum'],
    ['watch_time_min_1d','Minuti visti','sum'],
    ['likes_1d','Like','sum'],
  ],
  twitter: [
    ['followers_count','Follower','delta'],
    ['impressions_1d','Impression','sum'],
    ['engagements_1d','Engagement','sum'],
  ],
  linkedin: [
    ['followers_count','Follower','delta'],
    ['impressions_1d','Impression','sum'],
    ['reactions_1d','Reazioni','sum'],
  ],
  google_business: [
    ['views_1d','Visualizzazioni','sum'],
    ['calls_1d','Chiamate','sum'],
    ['website_clicks_1d','Click sito','sum'],
  ],
  ads: [
    ['spend','Spesa totale (€)','sum'],
    ['impressions','Impression','sum'],
    ['clicks','Click','sum'],
    ['conversions','Conversioni','sum'],
    ['ctr','CTR medio (%)','avg'],
    ['roas','ROAS medio','avg'],
  ],
};

/* ─── Helpers ──────────────────────────────────────────────────────────── */
const fmt  = n => new Intl.NumberFormat('it-IT').format(Math.round(n||0));
const fmtD = n => (n||0).toFixed(2);
const sumF = (ts, f) => ts.reduce((s, r) => s + (Number(r[f])||0), 0);
const avgF = (ts, f) => ts.length ? sumF(ts,f)/ts.length : 0;
const pct  = (a, b) => { a=Number(a)||0; b=Number(b)||0; if(!b) return null; return ((a-b)/Math.abs(b)*100).toFixed(1); };
const srcColor = src => COLORS[src] || [100, 110, 130];
const srcLabel = src => LABELS[src] || src;
const dateLabel = iso => new Date(iso).toLocaleDateString('it-IT', { day:'2-digit', month:'2-digit' });

/* ─── Palette PDF (light/print) ─────────────────────────────────────────── */
const C = {
  bg:      [7, 7, 26],      // cover dark bg
  bgLight: [248, 249, 252], // section bg
  border:  [220, 225, 235],
  text:    [30, 35, 50],
  muted:   [100, 110, 130],
  cyan:    [0, 180, 220],
  green:   [16, 185, 129],
  red:     [239, 68, 68],
  white:   [255, 255, 255],
};

/* ─── Cover page ───────────────────────────────────────────────────────── */
function drawCover(doc, { clienteLabel, days, channels, dateStr }) {
  const W = 210, margin = 18;

  // Dark header band
  doc.setFillColor(...C.bg);
  doc.rect(0, 0, W, 90, 'F');

  // Cyan accent bar (left edge)
  doc.setFillColor(...C.cyan);
  doc.rect(0, 0, 5, 90, 'F');

  // EdenFlow wordmark
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...C.cyan);
  doc.text('EDENFLOW', margin, 24);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(160, 170, 200);
  doc.text('Marketing Intelligence Platform', margin, 31);

  // Client name
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(26);
  doc.setTextColor(...C.white);
  const cName = clienteLabel || 'Cliente';
  doc.text(cName, margin, 58);

  // Subtitle
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(160, 170, 200);
  doc.text('Report Marketing Analytics', margin, 68);

  // Period + date
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...C.cyan);
  doc.setFillColor(15, 30, 55);
  doc.roundedRect(margin, 74, 58, 8, 2, 2, 'F');
  doc.text(`Periodo: ultimi ${days} giorni`, margin + 4, 79.2);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(120, 130, 160);
  doc.text(`Generato il ${dateStr}`, W - margin, 79, { align: 'right' });

  // Platform badge strip
  let bx = margin;
  const by = 95;
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'bold');
  channels.forEach(ch => {
    const col = srcColor(ch.source);
    const label = srcLabel(ch.source);
    const bw = doc.getTextWidth(label) + 10;
    doc.setFillColor(...col);
    doc.roundedRect(bx, by, bw, 7, 2, 2, 'F');
    doc.setTextColor(...C.white);
    doc.text(label, bx + 5, by + 5);
    bx += bw + 4;
  });

  // Summary section title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...C.text);
  doc.text('Panoramica del periodo', margin, 118);

  // Separator
  doc.setDrawColor(...C.border);
  doc.setLineWidth(0.5);
  doc.line(margin, 121, W - margin, 121);

  // Summary KPI grid (2 cards per platform, max 3 platforms)
  let gy = 127;
  const cardW = (W - margin * 2 - 10) / 2;
  let gi = 0;
  channels.slice(0, 4).forEach(ch => {
    const kpis = KPIS[ch.source] || [];
    const col = srcColor(ch.source);
    const ts = ch.timeseries || [];
    const latest = ch.latest || {};
    const first  = ch.first  || {};

    // Platform mini header
    doc.setFillColor(...col);
    doc.rect(margin, gy, 3, 10, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...col);
    doc.text(srcLabel(ch.source), margin + 6, gy + 7);
    gy += 14;

    // KPI pairs
    const pairs = [];
    kpis.slice(0, 4).forEach(([field, label, mode]) => {
      let val, prev;
      if (mode === 'sum') { val = sumF(ts, field); prev = null; }
      else if (mode === 'avg') { val = avgF(ts, field); prev = null; }
      else { val = Number(latest[field])||0; prev = Number(first[field])||0; }
      const delta = prev != null ? pct(val, prev) : null;
      pairs.push({ label, val, delta, mode });
    });

    const cols = Math.min(pairs.length, 4);
    const kw = (W - margin * 2 - (cols - 1) * 4) / cols;
    pairs.forEach((k, ki) => {
      const kx = margin + ki * (kw + 4);
      doc.setFillColor(...C.bgLight);
      doc.setDrawColor(...C.border);
      doc.setLineWidth(0.3);
      doc.roundedRect(kx, gy, kw, 16, 2, 2, 'FD');
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...C.muted);
      doc.text(k.label, kx + 4, gy + 5.5);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10.5);
      doc.setTextColor(...C.text);
      const displayVal = k.mode === 'avg' ? fmtD(k.val) : fmt(k.val);
      doc.text(displayVal, kx + 4, gy + 13);
      if (k.delta != null) {
        const dNum = Number(k.delta);
        doc.setFontSize(7.5);
        doc.setTextColor(...(dNum >= 0 ? C.green : C.red));
        doc.text((dNum >= 0 ? '+' : '') + k.delta + '%', kx + kw - 4, gy + 13, { align: 'right' });
      }
    });
    gy += 22;
    gi++;
  });
}

/* ─── Platform detail page ──────────────────────────────────────────────── */
function drawPlatformPage(doc, ch, days) {
  const W = 210, margin = 18;
  const col = srcColor(ch.source);
  const kpis = KPIS[ch.source] || [];
  const ts   = ch.timeseries || [];
  const latest = ch.latest || {};
  const first  = ch.first  || {};

  // Header band
  doc.setFillColor(...col);
  doc.rect(0, 0, W, 28, 'F');
  doc.setFillColor(0, 0, 0, 0.35);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(...C.white);
  doc.text(srcLabel(ch.source), margin, 18);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(230, 240, 255);
  doc.text(`Analisi degli ultimi ${days} giorni`, W - margin, 18, { align: 'right' });

  // ─── KPI summary table ───
  let y = 40;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...C.text);
  doc.text('Riepilogo KPI', margin, y);
  y += 5;

  const kpiRows = [];
  kpis.forEach(([field, label, mode]) => {
    let curr, prev;
    if (mode === 'sum')        { curr = sumF(ts, field); prev = null; }
    else if (mode === 'avg')   { curr = avgF(ts, field); prev = null; }
    else /* delta */           { curr = Number(latest[field])||0; prev = Number(first[field])||0; }
    const d = prev != null ? pct(curr, prev) : null;
    const displayVal = (mode === 'avg' && ch.source === 'ads' && field.includes('rate'))
      ? fmtD(curr) + '%' : (mode === 'avg' ? fmtD(curr) : fmt(curr));
    const trend = d != null ? (Number(d) >= 0 ? '▲ +' + d + '%' : '▼ ' + d + '%') : '—';
    const trendColor = d != null && Number(d) >= 0 ? '#0AB380' : '#EF4444';
    kpiRows.push([label, displayVal, trend, prev != null ? fmt(prev) : '—']);
  });

  autoTable(doc, {
    startY: y,
    head: [['Metrica', 'Valore attuale', 'Variazione vs inizio', 'Valore inizio periodo']],
    body: kpiRows,
    theme: 'grid',
    headStyles: { fillColor: col, textColor: 255, fontStyle: 'bold', fontSize: 8.5 },
    bodyStyles: { fontSize: 8.5, textColor: C.text },
    alternateRowStyles: { fillColor: C.bgLight },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 60 },
      1: { halign: 'right', cellWidth: 40 },
      2: { halign: 'right', cellWidth: 45 },
      3: { halign: 'right', cellWidth: 40 },
    },
    margin: { left: margin, right: margin },
    didParseCell: (data) => {
      if (data.column.index === 2 && data.section === 'body') {
        const txt = data.cell.raw || '';
        if (txt.startsWith('▲')) data.cell.styles.textColor = [10, 179, 128];
        else if (txt.startsWith('▼')) data.cell.styles.textColor = [239, 68, 68];
      }
    },
  });

  // ─── Time series table (last 10 snapshots) ───
  y = doc.lastAutoTable.finalY + 12;
  if (y > 230) { doc.addPage(); y = 20; }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...C.text);
  doc.text('Andamento giornaliero', margin, y);
  y += 5;

  const mainKpis = kpis.slice(0, 4);
  const tsSlice  = ts.slice(-15); // max 15 rows to fit

  const tsHead = ['Data', ...mainKpis.map(k => k[1])];
  const tsBody = tsSlice.map(row => [
    dateLabel(row.date),
    ...mainKpis.map(([field, , mode]) => {
      const v = Number(row[field]) || 0;
      return mode === 'avg' ? fmtD(v) : fmt(v);
    }),
  ]);

  autoTable(doc, {
    startY: y,
    head: [tsHead],
    body: tsBody,
    theme: 'striped',
    headStyles: { fillColor: col, textColor: 255, fontStyle: 'bold', fontSize: 8 },
    bodyStyles: { fontSize: 8, textColor: C.text },
    alternateRowStyles: { fillColor: C.bgLight },
    columnStyles: { 0: { fontStyle: 'bold' } },
    margin: { left: margin, right: margin },
  });

  // ─── Mini bar chart (reach or main metric) ───────────────────────────
  const chartY = doc.lastAutoTable.finalY + 10;
  if (chartY < 240 && ts.length > 1) {
    const mainField = mainKpis[1]?.[0] || mainKpis[0]?.[0];
    const mainLabel = mainKpis[1]?.[1] || mainKpis[0]?.[1];
    if (mainField) {
      drawMiniBarChart(doc, ts.slice(-30), mainField, mainLabel, margin, chartY, W - margin * 2, 30, col);
    }
  }
}

/* ─── Mini bar chart ────────────────────────────────────────────────────── */
function drawMiniBarChart(doc, ts, field, label, x, y, width, height, color) {
  const vals = ts.map(r => Number(r[field]) || 0);
  const max  = Math.max(...vals, 1);
  const n    = ts.length;
  const bw   = Math.max(1, (width - 2) / n - 1);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(60, 70, 90);
  doc.text(label + ' — andamento', x, y - 2);

  doc.setFillColor(...color, 0.15);
  doc.roundedRect(x, y, width, height, 2, 2, 'F');

  vals.forEach((v, i) => {
    const bh = (v / max) * (height - 6);
    const bx = x + 1 + i * (bw + 1);
    const by = y + height - 3 - bh;
    const alpha = 0.4 + (v / max) * 0.6;
    doc.setFillColor(color[0], color[1], color[2]);
    doc.setGState(new doc.GState({ opacity: alpha }));
    doc.rect(bx, by, bw, bh, 'F');
  });
  doc.setGState(new doc.GState({ opacity: 1 }));
}

/* ─── Footer ────────────────────────────────────────────────────────────── */
function addFooters(doc, clienteLabel, days) {
  const n = doc.getNumberOfPages();
  for (let i = 1; i <= n; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...C.muted);
    doc.line(18, 285, 192, 285);
    doc.text('EdenFlow — Marketing Analytics Report', 18, 290);
    doc.text(`${clienteLabel} · Ultimi ${days} giorni`, 105, 290, { align: 'center' });
    doc.text(`Pag. ${i} / ${n}`, 192, 290, { align: 'right' });
  }
}

/* ─── Entry point ───────────────────────────────────────────────────────── */
export function generateAnalyticsPDF({ clienteLabel, days, channels }) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const dateStr = new Date().toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });

  drawCover(doc, { clienteLabel, days, channels, dateStr });

  channels.forEach(ch => {
    doc.addPage();
    drawPlatformPage(doc, ch, days);
  });

  addFooters(doc, clienteLabel, days);

  const slug = (clienteLabel || 'report').replace(/[^a-z0-9]/gi, '-').toLowerCase();
  doc.save(`edenflow-analytics-${slug}-${days}g.pdf`);
}
